import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Trash2, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export function DeleteUserButton({ employee, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [blocked, setBlocked] = useState(null);
  const { setPeriod } = usePlanningPeriod();
  const navigate = useNavigate();
  const remove = async () => {
    setBusy(true); setError(""); setBlocked(null);
    try {
      await api.delete(`/admin/users/${employee.id}`);
      toast.success("Gebruiker definitief verwijderd"); setOpen(false); onDeleted();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(formatApiError(detail));
      if (detail?.code === "future_assignments") setBlocked(detail);
      if (e.response?.status === 503) onDeleted();
    } finally { setBusy(false); }
  };
  const goToPlanning = (service) => {
    setPeriod({ date_from: service.date, date_to: service.date }); setOpen(false);
    navigate(`/board?service=${encodeURIComponent(service.id)}`);
  };
  return <AlertDialog open={open} onOpenChange={(value) => {
    if (!busy) { setOpen(value); if (value) { setError(""); setBlocked(null); } }
  }}>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50" aria-label={`${employee.name} verwijderen`} data-testid={`delete-user-${employee.id}`}>
        <Trash2 className="w-4 h-4 mr-1" />Verwijderen
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="delete-user-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Gebruiker definitief verwijderen?</AlertDialogTitle>
        <AlertDialogDescription>
          <span className="block font-semibold text-foreground break-words mt-2" data-testid="delete-user-identity">{employee.name} · {employee.email}</span>
          <span className="block mt-3">Het account en de bijbehorende gegevens worden verwijderd. Toegang, uitnodigingslinks en agendasynchronisatie stoppen. Auditregistraties blijven zonder naam of e-mailadres bewaard. Dit kun je niet ongedaan maken.</span>
          <span className="block mt-2">Toekomstige en lopende diensten moeten eerst opnieuw zijn toegewezen.</span>
        </AlertDialogDescription>
      </AlertDialogHeader>
      {error && <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="delete-user-error">{error}</div>}
      {blocked && <div className="space-y-2" data-testid="delete-user-blockers">
        <p className="text-sm font-medium" data-testid="delete-user-blocker-count">{blocked.count} dienst(en) opnieuw toewijzen:</p>
        {blocked.services.map((service) => <Button key={service.id} variant="outline" className="w-full h-auto justify-start text-left whitespace-normal py-2" onClick={() => goToPlanning(service)} data-testid={`delete-user-reassign-${service.id}`}>
          <CalendarDays className="w-4 h-4 mr-2 shrink-0" /><span><strong>{service.name}</strong><span className="block text-xs text-muted-foreground">{service.date} · {service.start_time}–{service.end_time} · Open planbord</span></span>
        </Button>)}
        {blocked.count > blocked.services.length && <p className="text-xs text-muted-foreground">De eerste {blocked.services.length} diensten worden getoond.</p>}
      </div>}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={busy} data-testid="delete-user-cancel">Annuleren</AlertDialogCancel>
        <Button variant="destructive" disabled={busy || !!blocked} onClick={remove} data-testid="delete-user-confirm">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{busy ? "Verwijderen…" : "Definitief verwijderen"}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
}
